import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError } from "@/services/api";
import { adminApi } from "@/services/adminApi";

type AuthState = {
  username: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AdminAuthContext = createContext<AuthState | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [username, setUsername] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const requestId = useRef(0);

  const refresh = useCallback(async () => {
    const id = ++requestId.current;
    try {
      const session = await adminApi.me();
      if (id !== requestId.current) return;
      setUsername(session?.username ?? null);
    } catch (error) {
      if (id !== requestId.current) return;
      if (!(error instanceof ApiError) || error.status !== 401) throw error;
      setUsername(null);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = async (user: string, password: string) => {
    requestId.current += 1;
    const session = await adminApi.login(user, password);
    setUsername(session.username ?? user);
    setLoading(false);
  };

  const logout = async () => {
    requestId.current += 1;
    try {
      await adminApi.logout();
    } catch {
      /* Session may already be gone. */
    } finally {
      setUsername(null);
      setLoading(false);
    }
  };

  return (
    <AdminAuthContext.Provider value={{ username, loading, login, logout, refresh }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return context;
}
